import React from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import StatCard from '../components/cards/StatCard';
import { Target, Flag, Shield, Clock, Award, Users, Zap, Briefcase, User } from 'lucide-react';

const About = () => {
  return (
    <Layout>
      <PageHeader
        title="About TOTO INFRA"
        subtitle="From Specialized Foundations to Turnkey Industrial Infrastructure"
        image="/assets/image 4.png"
      />

      {/* Company Story */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <SectionHeader title="Our Journey" subtitle="A Legacy of Excellence" align="left" className="mb-8" />
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p>
                  TOTO INFRA started its journey as a specialized private limited company focused on foundation engineering. Over the last two decades, we have diversified into a full-spectrum infrastructure provider.
                </p>
                <p>
                  Today, we are a trusted partner for major steel plants, power plants, and complex civil infrastructure projects across India. Our growth is driven by our commitment to technology, quality, and safety.
                </p>
              </div>
            </div>
            <div data-aos="fade-left">
              <div className="bg-card border border-primary/20 p-10 rounded-xl relative">
                <div className="absolute -top-6 -left-6 text-6xl text-primary opacity-20 font-serif">"</div>
                <p className="text-xl font-serif italic text-foreground mb-6 relative z-10">
                  Our growth is a testament to our unwavering dedication to quality work and a public service mindset. We don't just build structures; we build the nation's backbone.
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-background" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Chairman's Message</h4>
                    <p className="text-sm text-primary">TOTO INFRA Group</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-card relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-background/50 backdrop-blur-sm p-10 rounded-xl border border-white/5 hover:border-primary/30 transition-all duration-500 group" data-aos="fade-up">
              <Target className="w-12 h-12 text-primary mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-serif font-bold text-foreground mb-4">Our Vision</h3>
              <p className="text-muted-foreground leading-relaxed">
                To be the benchmark in steel, power, and prestressing infrastructure, recognized globally for our engineering excellence and sustainable practices.
              </p>
            </div>
            <div className="bg-background/50 backdrop-blur-sm p-10 rounded-xl border border-white/5 hover:border-primary/30 transition-all duration-500 group" data-aos="fade-up" data-aos-delay="100">
              <Flag className="w-12 h-12 text-primary mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-serif font-bold text-foreground mb-4">Our Mission</h3>
              <p className="text-muted-foreground leading-relaxed">
                Deliver safe, sustainable, and high-quality work with professional ethics and a customer-centric approach, ensuring value for all stakeholders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader title="Core Values" subtitle="The Pillars of Our Success" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: 'Technology', desc: 'Embracing modern construction methods.' },
              { icon: Award, title: 'Quality', desc: 'Zero compromise on standards.' },
              { icon: Clock, title: 'Timely Completion', desc: 'Respecting project deadlines.' },
              { icon: Shield, title: 'Safety', desc: 'Safety first, always.' },
              { icon: Users, title: 'Integrity', desc: 'Honest and transparent dealings.' },
              { icon: Briefcase, title: 'Partnership', desc: 'Building long-term relationships.' },
            ].map((item, index) => (
              <div key={index} className="text-center p-6 group" data-aos="fade-up" data-aos-delay={index * 50}>
                <div className="w-16 h-16 mx-auto bg-card rounded-full flex items-center justify-center mb-4 border border-white/10 group-hover:border-primary group-hover:bg-primary group-hover:text-background transition-all duration-300">
                  <item.icon size={24} />
                </div>
                <h4 className="text-xl font-bold text-foreground mb-2">{item.title}</h4>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strengths */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-[#E5C560] to-primary opacity-90"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCard value="20+" label="Years Experience" />
            <StatCard value="50+" label="Major Projects" />
            <StatCard value="6" label="Key Sectors" />
            <StatCard value="100%" label="Client Satisfaction" />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
